import React, { useState, forwardRef, useImperativeHandle, useEffect } from "react";
import styles from "./MediaLibrary.module.css";
import FolderTree from "../../Components/FolderTree/FolderTree";
import FileFolderPreview from "../FileFolderPreview/FileFolderPreview";
import ImagePreview from "../ImagePreview/ImagePreview";
import FileService from "../../Services/fileService";
import { useToasts } from "../../LayoutComponents/ToastContext";
import i18n from "../../Services/i18n";

const MediaLibrary = forwardRef(({ onDirtyChange }, ref) => {
  const [folderTreeData, setFolderTreeData] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [fileBrowserData, setFileBrowserData] = useState([]);
  const [selectedFileId, setSelectedFileId] = useState(null);
  const [selectedThumbnail, setSelectedThumbnail] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const { addToast } = useToasts();

  const notifyDirtyChange = () => {
    onDirtyChange?.();
  };

  useEffect(() => {
    notifyDirtyChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFileId]);

  useImperativeHandle(ref, () => ({
    save: async () => {
      if (!selectedFileId) return { success: false };

      const result = await FileService.UploadFromLibrary(selectedFileId);

      return {
        success: result.success,
        id: selectedFileId,
        thumbnail: selectedThumbnail,
      };
    },

    isDirty: () => {
      return !!selectedFileId;
    },
  }));

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await FileService.getFolderBrowser();
        setFolderTreeData(data);
      } catch (ex) {
        addToast(i18n.t("Generic.ServerError"), "BACKENDERROR", "ERROR", true);
      }
    };

    fetchData();
  }, [addToast]);

  const handleFolderSelect = async (folder) => {
    if (!folder || folder.folderUId === selectedFolder?.folderUId) return;

    setSelectedFolder(folder);
    setSelectedFileId(null);
    setSelectedThumbnail(null);
    setPreviewUrl(null);
    notifyDirtyChange();

    const files = await FileService.getFileFolderPreview({
      FolderUid: folder.folderUId,
      FolderUidStrict: true,
    });

    setFileBrowserData(files);
  };

  const handleFileSelected = async (id) => {
    const url = await FileService.getFile(id);
    setPreviewUrl(url);

    const file = fileBrowserData.find((x) => x.id === id);
    setSelectedThumbnail(file?.thumbnail ?? null);
    setSelectedFileId(id);
    notifyDirtyChange();
  };

  return (
    <div className={styles.container}>
      <div className={styles.tree}>
        <FolderTree folderTreeData={folderTreeData} handleSelectFolder={handleFolderSelect} />
      </div>

      <div className={styles.browser}>
        <FileFolderPreview
          canEdit={false}
          canDelete={false}
          hideHeaderMenu={false}
          files={fileBrowserData}
          viewMode="icon"
          smallIcon
          onFileSelected={handleFileSelected}
        />
      </div>

      <div className={styles.preview}>
        {previewUrl ? (
          <ImagePreview imageUrl={previewUrl} />
        ) : (
          <div className={styles.emptyPreview}></div>
        )}
      </div>
    </div>
  );
});

export default MediaLibrary;
